import { inject, Injectable } from "@angular/core";
import { catchError, forkJoin, map, Observable, of, switchMap } from "rxjs";
import { Breadcrumb } from "./breadcrumb.type";
import { NavigationService } from "../navigation/navigation.service";
import { BreadcrumbHttpService } from "./breadcrumb-http.service";
import { BreadcrumbContext, NavigatedRoute, NavigationStack, RouteDataContext } from "../../types/navigation.type";
import { parseToObject } from "@shared/functions/map-object-parse";

@Injectable({
  providedIn: "root"
})
export class BreadcrumbService {
  private readonly _navigationService: NavigationService = inject(NavigationService);

  private readonly _http: BreadcrumbHttpService = inject(BreadcrumbHttpService);

  /**
   * Stream of the current Breadcrumbs
   */
  public breadcrumbs$: Observable<Breadcrumb[]> = this.initializeBreadcrumbsStream();

  private initializeBreadcrumbsStream (): Observable<Breadcrumb[]> {
    return this._navigationService.navigationStack$.pipe(
      map((navStack: NavigationStack) => {
        // eslint-disable-next-line @tseslint/no-unsafe-member-access
        const navigatedRoutes: NavigatedRoute[] = navStack.navigatedRoutes.filter((navRoute: NavigatedRoute) => navRoute.route.data?.[ "context" ]?.breadcrumb !== undefined);

        return {
          ...navStack,
          navigatedRoutes
        };
      }),
      switchMap((navStack: NavigationStack) => this.buildBreadcrumbs(navStack))
    );
  }

  private buildBreadcrumbs (navStack: NavigationStack): Observable<Breadcrumb[]> {
    const breadcrumbObservables: Array<Observable<Breadcrumb>>
            = navStack.navigatedRoutes.filter((navRoute: NavigatedRoute) => Boolean(navRoute.route.data)).map((navRoute: NavigatedRoute) => {
              const data: {
                apiPath?: string | undefined;
                "static"?: BreadcrumbContext;
                isTemplate?: boolean;
                staticCondition?(paramMap: Map<string, string>): boolean;
              } | undefined = (navRoute.route.data?.[ "context" ] as RouteDataContext).breadcrumb;
              const staticData: BreadcrumbContext | undefined = data[ "static" ];

              if (staticData && (!data.staticCondition || data.staticCondition(navStack.paramMap))) {
                if (staticData.translationInfo) {
                  return of<Breadcrumb>({
                    translationInfo: staticData.translationInfo,
                    templateData: staticData.isTemplate ? parseToObject(navStack.paramMap) : undefined,
                    segment: navRoute.instancePath
                  });
                } else if (staticData.label) {
                  return of({
                    label: staticData.label,
                    segment: navRoute.instancePath
                  });
                }

                return of();
              }

              /* eslint-disable-next-line @tseslint/no-non-null-assertion */
              return this.request(data.apiPath!, navStack.paramMap).pipe(
                map((response: BreadcrumbContext) => ({
                  label: response.label,
                  segment: navRoute.instancePath
                })), catchError(() => of<Breadcrumb>({ label: "undefined", segment: navRoute.instancePath }))
              );
            });

    return breadcrumbObservables.length > 0 ? forkJoin(breadcrumbObservables) : of([]);
  }

  private request (path: string, paramMap: Map<string, string>): Observable<BreadcrumbContext> {
    /* eslint-disable-next-line prefer-named-capture-group */
    const url: string = path.replaceAll(/{(\w+)}/g, (_: string, key: string) => paramMap.get(key) ?? "wrong paramName"),
      request: ((url: string) => Observable<BreadcrumbContext>) | undefined = this._http.requests.get(path);

    if (request)
      return request(url);

    throw new Error(`No request found for path: ${ path }`);
  }
}
